import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Spinner,
  Modal,
  Table,
} from "react-bootstrap";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import { FaSearch, FaUndo, FaMoneyBillWave, FaEdit } from "react-icons/fa";
import { Link } from "react-router-dom";
import refundService from "../../services/refundService";
import orderService from "../../services/orderService";
import paymentService from "../../services/paymentService";
import api from "../../services/api";
import InfoPill from "../Common/InfoPill";
import { toast } from "react-toastify";
import "./Report.css";

const RefundsReport = () => {
  const [loading, setLoading] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [refundForm, setRefundForm] = useState({
    order_id: "",
    payment_id: "",
    refund_amount: "",
    refund_mode: "cash",
    refund_type: "manual",
    reason: "",
    notes: "",
  });
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [booking3PL, setBooking3PL] = useState(false);
  const [reverseTracking, setReverseTracking] = useState(null);
  const [loadingReverseTracking, setLoadingReverseTracking] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    refund_status: "",
    refund_type: "",
    refund_mode: "",
    order_id: "",
    payment_id: "",
    customer_id: "",
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    loadRefunds();
    loadOrders();
  }, []);

  useEffect(() => {
    loadRefunds();
  }, [filters]);

  const loadRefunds = async () => {
    try {
      setLoading(true);
      const cleanedFilters = {};
      Object.keys(filters).forEach((key) => {
        if (filters[key] !== "" && filters[key] !== null) {
          cleanedFilters[key] = filters[key];
        }
      });

      const response = await refundService.getAllRefunds(cleanedFilters);
      setRefunds(response.data || []);
      setSummary(response.summary || null);
    } catch (error) {
      console.error("Error loading refunds:", error);
      toast.error("Failed to load refunds");
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const response = await orderService.getAllOrders({ limit: 1000 });
      setOrders(response.orders || response.data || []);
    } catch (error) {
      console.error("Error loading orders:", error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      refund_status: "",
      refund_type: "",
      refund_mode: "",
      order_id: "",
      payment_id: "",
      customer_id: "",
      start_date: "",
      end_date: "",
    });
  };

  const handleCreateRefund = async () => {
    try {
      if (!refundForm.order_id && !refundForm.payment_id) {
        toast.error("Please select either Order ID or Payment ID");
        return;
      }
      if (!refundForm.refund_amount || refundForm.refund_amount <= 0) {
        toast.error("Please enter a valid refund amount");
        return;
      }

      // Validate UUID format
      if (refundForm.payment_id) {
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(refundForm.payment_id)) {
          toast.error("Invalid Payment ID format. Please use a valid UUID.");
          return;
        }
      }
      if (refundForm.order_id) {
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (!uuidRegex.test(refundForm.order_id)) {
          toast.error("Invalid Order ID format. Please use a valid UUID.");
          return;
        }
      }

      setLoading(true);

      // Log request details for debugging
      console.log("🔄 [REFUND] Creating refund:", {
        type: refundForm.refund_type,
        payment_id: refundForm.payment_id,
        order_id: refundForm.order_id,
        amount: refundForm.refund_amount,
      });

      // Check token before request
      const token = localStorage.getItem("authToken");
      const refreshToken = localStorage.getItem("refreshToken");
      console.log("🔍 [REFUND] Token check:", {
        hasToken: !!token,
        hasRefreshToken: !!refreshToken,
      });

      if (refundForm.refund_type === "automatic") {
        const response = await refundService.createAutomaticRefund({
          payment_id: refundForm.payment_id,
          refund_amount: parseFloat(refundForm.refund_amount),
          reason: refundForm.reason,
          notes: refundForm.notes,
        });
        console.log("✅ [REFUND] Automatic refund created:", response);
        toast.success("Automatic refund processed successfully");
      } else {
        const response = await refundService.createManualRefund({
          order_id: refundForm.order_id || null,
          payment_id: refundForm.payment_id || null,
          refund_amount: parseFloat(refundForm.refund_amount),
          refund_mode: refundForm.refund_mode,
          reason: refundForm.reason,
          notes: refundForm.notes,
        });
        console.log("✅ [REFUND] Manual refund created:", response);
        toast.success("Manual refund created successfully");
      }

      setShowRefundModal(false);
      setRefundForm({
        order_id: "",
        payment_id: "",
        refund_amount: "",
        refund_mode: "cash",
        refund_type: "manual",
        reason: "",
        notes: "",
      });
      loadRefunds();
    } catch (error) {
      console.error("❌ [REFUND] Error creating refund:", error);
      console.error("❌ [REFUND] Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.response?.data?.message,
        url: error.config?.url,
        method: error.config?.method,
        hasResponse: !!error.response,
        hasRequest: !!error.request,
      });

      // Check if it's a 401 error - interceptor should handle it, but show user-friendly message
      if (error.response?.status === 401) {
        const errorMessage =
          error.response?.data?.message || "Authentication failed";
        if (
          errorMessage.includes("expired") ||
          errorMessage.includes("Token expired")
        ) {
          toast.error(
            "Your session has expired. The system will try to refresh your token automatically. Please wait a moment and try again."
          );
          // The interceptor should have already tried to refresh
          // Give it a moment, then user can retry
        } else {
          toast.error("Authentication failed. Please login again.");
          setTimeout(() => {
            window.location.href = "/login";
          }, 2000);
        }
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to create refunds.");
      } else if (error.response?.status === 400) {
        toast.error(
          error.response?.data?.message ||
            "Invalid request. Please check your input."
        );
      } else if (error.response?.status === 404) {
        toast.error(error.response?.data?.message || "Resource not found.");
      } else {
        toast.error(
          error.response?.data?.message ||
            "Failed to create refund. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (refund) => {
    try {
      const response = await refundService.getRefundById(refund.id);
      const data = response.data || response;
      setSelectedRefund(data);
      setReverseTracking(null);
      setShowDetailsModal(true);
      if (data.reverse_awb_number) {
        fetchReverseTracking(data.reverse_awb_number);
      }
    } catch (error) {
      console.error("Error fetching refund details:", error);
      toast.error("Failed to load refund details");
    }
  };

  const fetchReverseTracking = async (awb) => {
    if (!awb) return;
    setLoadingReverseTracking(true);
    try {
      const response = await orderService.getShadowfaxReverseTracking(awb);
      if (response && response.success) {
        setReverseTracking(response.data);
      }
    } catch (err) {
      console.error("Error fetching reverse tracking:", err);
    } finally {
      setLoadingReverseTracking(false);
    }
  };

  const handleBookReversePickup = async () => {
    if (!selectedRefund) return;
    try {
      setBooking3PL(true);

      // Fetch order details to check serviceability
      const orderRes = await orderService.getOrderById(selectedRefund.order_id);
      const orderData = orderRes.order || orderRes.data?.order || orderRes;
      const pincode = orderData.shipping_address_details?.pincode || 
                      orderData.shipping_address?.pincode ||
                      (orderData.shipping_address_details?.address_line
                        ? String(orderData.shipping_address_details.address_line).match(/\d{6}/)?.[0]
                        : null);

      if (!pincode) {
        toast.error("Could not find a valid 6-digit delivery pincode for this customer!");
        return;
      }

      // Check serviceability
      const serviceRes = await orderService.checkShadowfaxPickupServiceability(pincode);
      if (!serviceRes.serviceable) {
        toast.error(`Pincode ${pincode} is not serviceable for pickups by Shadowfax!`);
        return;
      }

      // Book return pickup
      const response = await api.post(`/api/shadowfax/pickup/create`, { refund_id: selectedRefund.id });
      toast.success(response.data?.message || "Reverse pickup shipment scheduled successfully with Shadowfax!");

      // Reload refund details
      const refDetails = await refundService.getRefundById(selectedRefund.id);
      const refData = refDetails.data || refDetails;
      setSelectedRefund(refData);
      if (refData.reverse_awb_number) {
        fetchReverseTracking(refData.reverse_awb_number);
      }
    } catch (err) {
      console.error("Error booking reverse pickup:", err);
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to book reverse pickup");
    } finally {
      setBooking3PL(false);
    }
  };

  const handleCreateRefundFromRecord = async (refund) => {
    try {
      // Use payment data from refund record (backend includes payment_amount and payment_mode)
      const paymentAmount = refund.payment_amount || "";
      const paymentMode = refund.payment_mode || "cash";

      // Pre-fill form with refund record data
      // Note: We want to create a NEW refund, so we use the original payment amount
      setRefundForm({
        order_id: refund.order_id || "",
        payment_id: refund.payment_id || "",
        refund_amount: paymentAmount || "", // Original payment amount paid at order time
        refund_mode: paymentMode === "online" ? "online" : "cash",
        refund_type: paymentMode === "online" ? "automatic" : "manual",
        reason: "",
        notes: "",
      });
      setShowRefundModal(true);
      toast.info(
        "Form pre-filled with payment details. Please review and adjust the refund amount if needed."
      );
    } catch (error) {
      console.error("Error preparing refund form:", error);
      toast.error("Failed to prepare refund form");
    }
  };

  const getStatusBadge = (status) => {
    const key = status?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    const statusConfig = {
      pending: { variant: "warning", text: "Pending" },
      processing: { variant: "info", text: "Processing" },
      completed: { variant: "success", text: "Completed" },
      failed: { variant: "danger", text: "Failed" },
      cancelled: { variant: "secondary", text: "Cancelled" },
      refunded: { variant: "warning", text: "Refunded" },
    };
    const config = statusConfig[key] || {
      variant: "secondary",
      text: status || "-",
    };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getTypeBadge = (type) => {
    const key = type?.toString().toLowerCase().trim().replace(/[\s-]+/g, "_");
    const typeConfig = {
      manual: { variant: "primary", text: "Manual" },
      automatic: { variant: "success", text: "Automatic" },
      partial: { variant: "info", text: "Partial" },
    };
    const config = typeConfig[key] || { variant: "secondary", text: type || "-" };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const tableHeaders = [
    { prop: "id", title: "Refund ID", isSortable: true },
    { prop: "order_id", title: "Order ID", isSortable: true },
    { prop: "customer_name", title: "Customer", isSortable: true },
    { prop: "refund_amount", title: "Amount", isSortable: true },
    { prop: "refund_status", title: "Status", isSortable: true },
    { prop: "refund_type", title: "Type", isSortable: true },
    { prop: "refund_mode", title: "Mode", isSortable: true },
    { prop: "created_at", title: "Created At", isSortable: true },
    { prop: "actions", title: "Actions", cell: () => null },
  ];

  const tableData = refunds.map((refund) => ({
    id: refund.id?.substring(0, 8) + "...",
    order_id: refund.order_id ? (
      <Link to={`/admin/orders/${refund.order_id}`}>
        {refund.order_id?.substring(0, 8) + "..."}
      </Link>
    ) : (
      "N/A"
    ),
    customer_name:
      refund.first_name && refund.last_name
        ? `${refund.first_name} ${refund.last_name}`
        : refund.customer_phone || "N/A",
    refund_amount: `₹${parseFloat(refund.refund_amount || 0).toFixed(2)}`,
    refund_status: getStatusBadge(refund.refund_status),
    refund_type: getTypeBadge(refund.refund_type),
    refund_mode: refund.refund_mode || "N/A",
    created_at: new Date(refund.created_at).toLocaleDateString(),
    actions: (
      <Button
        size="sm"
        variant="outline-primary"
        onClick={() => handleViewDetails(refund)}
      >
        View
      </Button>
    ),
  }));

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h4 className="mb-0">
                <FaUndo className="me-2" />
                Refunds Report
              </h4>
            </Card.Header>
            <Card.Body>
              {/* Summary Cards */}
              {summary && (
                <Row className="mb-4">
                  <Col md={3}>
                    <InfoPill
                      label="Total Refunds"
                      value={summary.total_refunds || 0}
                      icon={<FaUndo />}
                      variant="primary"
                    />
                  </Col>
                  <Col md={3}>
                    <InfoPill
                      label="Total Amount"
                      value={`₹${parseFloat(
                        summary.total_refund_amount || 0
                      ).toFixed(2)}`}
                      icon={<FaMoneyBillWave />}
                      variant="success"
                    />
                  </Col>
                  <Col md={3}>
                    <InfoPill
                      label="Completed"
                      value={summary.completed_count || 0}
                      icon={<FaEdit />}
                      variant="success"
                    />
                  </Col>
                  <Col md={3}>
                    <InfoPill
                      label="Pending"
                      value={summary.pending_count || 0}
                      icon={<FaEdit />}
                      variant="warning"
                    />
                  </Col>
                </Row>
              )}

              {/* Filters */}
              <Card className="mb-4">
                <Card.Header>
                  <h5 className="mb-0">Filters</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          name="refund_status"
                          value={filters.refund_status}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Statuses</option>
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="completed">Completed</option>
                          <option value="failed">Failed</option>
                          <option value="cancelled">Cancelled</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Type</Form.Label>
                        <Form.Select
                          name="refund_type"
                          value={filters.refund_type}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Types</option>
                          <option value="manual">Manual</option>
                          <option value="automatic">Automatic</option>
                          <option value="partial">Partial</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Mode</Form.Label>
                        <Form.Select
                          name="refund_mode"
                          value={filters.refund_mode}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Modes</option>
                          <option value="cash">Cash</option>
                          <option value="online">Online</option>
                          <option value="wallet">Wallet</option>
                          <option value="bank_transfer">Bank Transfer</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="start_date"
                          value={filters.start_date}
                          onChange={handleFilterChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="end_date"
                          value={filters.end_date}
                          onChange={handleFilterChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                      <Button
                        variant="outline-secondary"
                        onClick={handleResetFilters}
                      >
                        Reset Filters
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Refunds Table */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <DatatableWrapper
                  body={tableData}
                  headers={tableHeaders}
                  paginationOptionsProps={{
                    initialState: {
                      rowsPerPage: 10,
                      options: [5, 10, 15, 20],
                    },
                  }}
                >
                  <Row className="mb-3">
                    <Col
                      xs={12}
                      lg={4}
                      className="d-flex flex-col justify-content-end align-items-end order-3 order-lg-2"
                    >
                      <Filter placeholder="Search refunds..." />
                    </Col>
                    <Col
                      xs={12}
                      sm={6}
                      lg={4}
                      className="d-flex flex-col justify-content-lg-center align-items-center justify-content-sm-start mb-3 mb-lg-0 order-2 order-lg-3"
                    >
                      <PaginationOptions />
                    </Col>
                  </Row>
                  <Table>
                    <TableHeader />
                    <TableBody />
                  </Table>
                  <Row>
                    <Col
                      xs={12}
                      sm={6}
                      lg={4}
                      className="d-flex flex-col justify-content-end align-items-end"
                    >
                      <Pagination classes={{
                        button: "bg-dark",
                      }} />
                    </Col>
                  </Row>
                </DatatableWrapper>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Refund Modal */}
      <Modal
        show={showRefundModal}
        onHide={() => setShowRefundModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Refund</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Refund Type</Form.Label>
                  <Form.Select
                    value={refundForm.refund_type}
                    onChange={(e) =>
                      setRefundForm({
                        ...refundForm,
                        refund_type: e.target.value,
                      })
                    }
                  >
                    <option value="manual">Manual</option>
                    <option value="automatic">Automatic (Razorpay)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {refundForm.refund_type === "automatic"
                      ? "Payment ID *"
                      : "Order ID"}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={
                      refundForm.refund_type === "automatic"
                        ? "Enter Payment ID"
                        : "Enter Order ID"
                    }
                    value={
                      refundForm.refund_type === "automatic"
                        ? refundForm.payment_id
                        : refundForm.order_id
                    }
                    onChange={(e) =>
                      setRefundForm({
                        ...refundForm,
                        [refundForm.refund_type === "automatic"
                          ? "payment_id"
                          : "order_id"]: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
              {refundForm.refund_type === "manual" && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Payment ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter Payment ID (optional)"
                      value={refundForm.payment_id}
                      onChange={(e) =>
                        setRefundForm({
                          ...refundForm,
                          payment_id: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              )}
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Refund Amount *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter refund amount"
                    value={refundForm.refund_amount}
                    onChange={(e) =>
                      setRefundForm({
                        ...refundForm,
                        refund_amount: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
              {refundForm.refund_type === "manual" && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Refund Mode</Form.Label>
                    <Form.Select
                      value={refundForm.refund_mode}
                      onChange={(e) =>
                        setRefundForm({
                          ...refundForm,
                          refund_mode: e.target.value,
                        })
                      }
                    >
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                      <option value="wallet">Wallet</option>
                      <option value="bank_transfer">Bank Transfer</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Reason</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter refund reason"
                    value={refundForm.reason}
                    onChange={(e) =>
                      setRefundForm({ ...refundForm, reason: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Additional notes (optional)"
                    value={refundForm.notes}
                    onChange={(e) =>
                      setRefundForm({ ...refundForm, notes: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRefundModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateRefund}
            disabled={loading}
          >
            {loading ? "Processing..." : "Create Refund"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Refund Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Refund Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRefund && (
            <>
              <Table striped bordered hover>
              <tbody>
                <tr>
                  <th>Refund ID</th>
                  <td>{selectedRefund.id}</td>
                </tr>
                <tr>
                  <th>Order ID</th>
                  <td>
                    {selectedRefund.order_id ? (
                      <Link to={`/admin/orders/${selectedRefund.order_id}`}>
                        {selectedRefund.order_id}
                      </Link>
                    ) : (
                      "N/A"
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Refund Amount</th>
                  <td>
                    ₹{parseFloat(selectedRefund.refund_amount || 0).toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>{getStatusBadge(selectedRefund.refund_status)}</td>
                </tr>
                <tr>
                  <th>Type</th>
                  <td>{getTypeBadge(selectedRefund.refund_type)}</td>
                </tr>
                <tr>
                  <th>Mode</th>
                  <td>{selectedRefund.refund_mode || "N/A"}</td>
                </tr>
                <tr>
                  <th>Reason</th>
                  <td>{selectedRefund.reason || "N/A"}</td>
                </tr>
                <tr>
                  <th>Notes</th>
                  <td>{selectedRefund.notes || "N/A"}</td>
                </tr>
                <tr>
                  <th>Razorpay Refund ID</th>
                  <td>{selectedRefund.razorpay_refund_id || "N/A"}</td>
                </tr>
                <tr>
                  <th>Transaction ID</th>
                  <td>{selectedRefund.transaction_id || "N/A"}</td>
                </tr>
                <tr>
                  <th>Customer</th>
                  <td>
                    {selectedRefund.first_name && selectedRefund.last_name
                      ? `${selectedRefund.first_name} ${selectedRefund.last_name}`
                      : selectedRefund.customer_phone || "N/A"}
                  </td>
                </tr>
                <tr>
                  <th>Processed By</th>
                  <td>
                    {selectedRefund.processed_by_first_name &&
                    selectedRefund.processed_by_last_name
                      ? `${selectedRefund.processed_by_first_name} ${selectedRefund.processed_by_last_name}`
                      : "N/A"}
                  </td>
                </tr>
                <tr>
                  <th>Created At</th>
                  <td>
                    {new Date(selectedRefund.created_at).toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <th>Processed At</th>
                  <td>
                    {selectedRefund.processed_at
                      ? new Date(selectedRefund.processed_at).toLocaleString()
                      : "N/A"}
                  </td>
                </tr>
              </tbody>
            </Table>

            {/* 3PL Reverse Logistics section */}
            <div className="mt-4 p-3 bg-light rounded border text-start">
              <h6 className="fw-bold text-dark border-bottom pb-2 d-flex align-items-center gap-2">
                <i className="fas fa-truck-loading"></i> Reverse Pickup & Returns (Shadowfax)
              </h6>

              {selectedRefund.reverse_awb_number ? (
                <Row className="mt-2">
                  <Col md={6}>
                    <div className="mb-2">
                      <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>Reverse AWB</span>
                      <code className="text-primary fw-bold px-2 py-0.5 bg-white border rounded">
                        {selectedRefund.reverse_awb_number}
                      </code>
                    </div>
                    <div className="mb-2">
                      <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>Shipment Status</span>
                      <span className="badge bg-info text-dark" style={{ fontSize: '0.8rem' }}>
                        {selectedRefund.reverse_shipment_status || "New"}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>QC Quality Check</span>
                      <span className="badge bg-success" style={{ fontSize: '0.8rem' }}>
                        {selectedRefund.qc_status || "Pending"}
                      </span>
                    </div>
                    {selectedRefund.reverse_label_url && (
                      <div className="mt-3">
                        <a
                          href={selectedRefund.reverse_label_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary fw-bold"
                        >
                          <i className="fas fa-file-pdf"></i> Download Shipping Label
                        </a>
                      </div>
                    )}
                  </Col>

                  <Col md={6} className="border-start">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="small fw-bold text-muted uppercase" style={{ fontSize: '0.75rem' }}>Real-time Return Tracker</span>
                      <Button
                        size="xs"
                        variant="link"
                        className="p-0 text-decoration-none text-primary fw-bold"
                        style={{ fontSize: "11px" }}
                        onClick={() => fetchReverseTracking(selectedRefund.reverse_awb_number)}
                        disabled={loadingReverseTracking}
                      >
                        <i className={`fas fa-sync-alt ${loadingReverseTracking ? 'fa-spin' : ''}`}></i> Refresh
                      </Button>
                    </div>

                    {loadingReverseTracking && (
                      <div className="text-center py-3 text-muted small">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Fetching live tracking...
                      </div>
                    )}

                    {!loadingReverseTracking && reverseTracking && (
                      <div className="position-relative ps-3 border-start ms-2 mt-2" style={{ borderColor: '#ddd' }}>
                        {(() => {
                          const events = reverseTracking.order_details?.tracking_details || 
                                         reverseTracking.tracking_details || 
                                         reverseTracking.pickup_request_state_histories || 
                                         reverseTracking.delivery_request_state_histories || 
                                         [];

                          if (events.length === 0) {
                            return (
                              <div className="position-relative">
                                <div className="position-absolute bg-primary rounded-circle" style={{ width: '8px', height: '8px', left: '-19px', top: '6px' }}></div>
                                <span className="small fw-semibold d-block text-dark" style={{ fontSize: '0.8rem' }}>Registered</span>
                                <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>Pickup scheduled, pending rider dispatch</span>
                              </div>
                            );
                          }

                          return events.map((ev, idx) => {
                            const timestamp = ev.event_timestamp || ev.timestamp || ev.created_at;
                            const formattedTime = timestamp ? new Date(timestamp).toLocaleString() : '';
                            return (
                              <div key={idx} className="position-relative mb-2">
                                <div className="position-absolute bg-success rounded-circle" style={{ width: '6px', height: '6px', left: '-18px', top: '7px' }}></div>
                                <span className="small fw-bold d-block text-dark" style={{ fontSize: '0.8rem' }}>{ev.status || ev.state || ev.activity}</span>
                                {(ev.current_location || ev.location) && (
                                  <span className="small text-muted d-block" style={{ fontSize: '0.75rem' }}>Location: {ev.current_location || ev.location}</span>
                                )}
                                {formattedTime && (
                                  <span className="text-muted d-block" style={{ fontSize: '0.7rem' }}>{formattedTime}</span>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </Col>
                </Row>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted small mb-3">No reverse pickup scheduled for this return yet.</p>
                  {selectedRefund.refund_status !== "completed" && selectedRefund.refund_status !== "cancelled" ? (
                    <Button
                      variant="primary"
                      size="sm"
                      className="fw-bold text-white px-3"
                      onClick={handleBookReversePickup}
                      disabled={booking3PL}
                    >
                      {booking3PL ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Booking Pickup...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-shipping-fast"></i> Book Return Pickup (Shadowfax)
                        </>
                      )}
                    </Button>
                  ) : (
                    <span className="text-muted small italic">Pickup cannot be scheduled because refund is completed or cancelled.</span>
                  )}
                </div>
              )}
            </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDetailsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RefundsReport;
